// Jahresabschluss: Excel-Jahresrechnung aus MILKEE plus Pauschalen und Abschreibungen (Server, Web und App).

/** Blätter der Excel-Vorlage in ihrer Reihenfolge. `name` ist der Blattname in der Datei. */
export const CLOSING_SHEETS = [
  { key: "erloes", name: "Erlös Leistungen-Warenverkauf", label: "Erlös aus Leistungen" },
  { key: "waren", name: "Wareneinkauf, Dienstleistung", label: "Waren- und Dienstleistungsaufwand" },
  { key: "miete", name: "Miete", label: "Miete" },
  { key: "telefon", name: "Telefon, EDV", label: "Telefon, EDV" },
  { key: "buero", name: "Büro, Verwaltung", label: "Büromaterial, Verwaltung" },
  { key: "fahrzeug", name: "Fahrzeug", label: "Fahrzeug- & Transportaufwand" },
  { key: "buchfuehrung", name: "Buchführung", label: "Buchführung, Beratung" },
  { key: "reise", name: "Reisespesen", label: "Reisespesen, Kundenbetreuung" },
  { key: "finanz", name: "Finanzaufwand", label: "Finanzaufwand" },
  { key: "abschreibung", name: "Abschreibungen", label: "Abschreibungen" },
  { key: "sva", name: "SVA Beiträge", label: "SVA Beiträge" },
] as const;
export type SheetKey = (typeof CLOSING_SHEETS)[number]["key"];
/** Blätter, auf die Buchungen und Pauschalen gehen (Abschreibungen rechnet das Tool selbst) */
export type EntrySheet = Exclude<SheetKey, "abschreibung">;
export const ENTRY_SHEETS = CLOSING_SHEETS.filter((s) => s.key !== "abschreibung").map((s) => s.key) as EntrySheet[];
/** Wohin ein MILKEE-Konto zählt: ein Blatt, «Anlage» (wird abgeschrieben) oder gar nicht */
export type AccountTarget = EntrySheet | "anlage" | "ignore";
export const TARGET_LABEL: Record<AccountTarget, string> = {
  ...(Object.fromEntries(CLOSING_SHEETS.map((s) => [s.key, s.label])) as Record<SheetKey, string>),
  anlage: "Anschaffung (wird abgeschrieben)",
  ignore: "Nicht berücksichtigen",
};

/** Zeilen pro Blatt in der Vorlage (Zeile 5 bis 59) */
export const SHEET_ROWS = 55;

export interface ClosingItem {
  id: string;
  sheet: EntrySheet;
  title: string;
  /** Spalte «Debitor/Kreditor» */
  party: string;
  amount: number;
  /** pro Monat oder einmal im Jahr */
  per: "month" | "once";
  /** Anzahl Monate (nur pro Monat), ab Beginn des Zeitraums */
  months: number;
  /** pro Monat: jede Monat eine eigene Zeile (wie Miete 2025) statt einer Sammelzeile «8 Mte. à Fr. …» */
  rows: boolean;
  /** einmalig: Datum (leer = Ende des Zeitraums) */
  date: string;
}

export interface ClosingAsset {
  id: string;
  title: string;
  party: string;
  /** Kaufdatum JJJJ-MM-TT, leer = «Diverse» */
  date: string;
  cost: number;
  /** Jahr, ab dem die Anschaffung abgeschrieben wird */
  year: number;
  /** Anfangsbestand: zählt zum «Buchwert per 01.01.» statt als Zugang */
  opening: boolean;
  /** Gruppe in der Excel (z. B. Filmeinrichtung), id aus `ClosingGroup` */
  group: string;
}

/** Gruppe im Blatt «Abschreibungen», wie 2025: eine Zeile «Diverse | Filmeinrichtung | Diverse | Summe» */
export interface ClosingGroup {
  id: string;
  name: string;
  /** Stichwörter, an denen eine Buchung automatisch erkannt wird */
  keywords: string[];
}

/** Buchung aus «Waren und Dienstleistungen» oder einem Anlagekonto: Anschaffung (Gruppe) oder Aufwand */
export interface ClosingCandidate {
  id: string;
  date: string;
  title: string;
  party: string;
  account: string;
  amount: number;
  /** Gruppen-id oder «aufwand» (bleibt auf dem Blatt Waren und Dienstleistungen) */
  decision: string;
  /** automatisch entschieden (nicht von Hand) */
  auto: boolean;
  reason: string;
}

export type DepMethod = "degressive" | "linear";
export interface ClosingYear {
  from: string;
  to: string;
  method: DepMethod;
  rate: number;
  items: ClosingItem[];
}

export interface ClosingRow {
  /** JJJJ-MM-TT oder null für «Diverse» */
  date: string | null;
  title: string;
  party: string;
  amount: number;
  source: "milkee" | "pauschal" | "anlage";
}

export interface DepYear {
  year: number;
  method: DepMethod;
  rate: number;
  start: number;
  additions: number;
  dep: number;
  end: number;
}

export interface ClosingView {
  year: number;
  /** Einstellungen dieses Jahres (gespeichert oder vorgeschlagen) */
  config: ClosingYear;
  saved: boolean;
  milkee: boolean;
  sheets: { key: SheetKey; name: string; label: string; rows: ClosingRow[]; total: number; grouped: boolean }[];
  result: { income: number; goods: number; gross: number; costs: number; profit: number };
  /** alle Anschaffungen bis zu diesem Jahr; aus MILKEE mit `milkee: true` (nicht bearbeitbar) */
  assets: (ClosingAsset & { milkee?: boolean })[];
  depreciation: { years: DepYear[]; current: DepYear; preview: Record<DepMethod, number> };
  accounts: { number: number; name: string; target: AccountTarget | null; auto: boolean; sum: number; count: number }[];
  groups: (ClosingGroup & { total: number; count: number })[];
  /** Buchungen dieses Jahres, die Anschaffung oder Aufwand sein können */
  candidates: ClosingCandidate[];
  warnings: string[];
  fileName: string;
}

/** Vorschläge aus der Jahresrechnung 2025, die man mit einem Klick als Pauschale übernehmen kann */
export const ITEM_SUGGESTIONS: Omit<ClosingItem, "id" | "months">[] = [
  { sheet: "waren", title: "Adobe Creative Cloud", party: "", amount: 76.65, per: "month", rows: false, date: "" },
  { sheet: "waren", title: "Artist Lizenz", party: "Jahresgebühr", amount: 299, per: "once", rows: false, date: "" },
  { sheet: "waren", title: "Google Drive Lizenz", party: "Jahresgebühr", amount: 21, per: "once", rows: false, date: "" },
  { sheet: "buchfuehrung", title: "MILKEE Buchhaltungslizenz", party: "", amount: 21.51, per: "month", rows: false, date: "" },
  { sheet: "finanz", title: "PostFinance Geschäftskonto", party: "", amount: 5, per: "month", rows: false, date: "" },
];

export const sheetLabel = (k: SheetKey) => CLOSING_SHEETS.find((s) => s.key === k)?.label ?? k;
